import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { moviesMap, movieDetails, categories } from '@/mocks/movies';
import useSEO from '@/hooks/useSEO';
import type { MovieDetail } from '@/mocks/movies';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const movieId = Number(id);
  const movie = moviesMap[movieId];
  const detail: MovieDetail | undefined = movieDetails[movieId];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [movieId]);

  useSEO({
    title: movie ? `${movie.title} - 人人影院免费高清在线观看` : '影片未找到 - 人人影院',
    description: movie ? `人人影院提供${movie.title}免费高清在线观看，${movie.description}。导演：${movie.director}，主演：${movie.actors.join('、')}。` : '影片未找到',
    keywords: movie ? `${movie.title},${movie.title}在线观看,${movie.title}免费观看,${movie.subcategory},人人影院` : '人人影院',
    canonicalUrl: `https://renrencinema.com/detail/${movieId}`,
    ogTitle: movie ? `${movie.title} - 人人影院免费高清在线观看` : '人人影院',
    ogDescription: movie ? movie.description : '',
    ogType: 'video.movie',
    ogUrl: `https://renrencinema.com/detail/${movieId}`,
    structuredData: movie ? [
      {
        "@context": "https://schema.org",
        "@type": "Movie",
        "name": movie.title,
        "description": movie.description,
        "dateCreated": String(movie.year),
        "director": { "@type": "Person", "name": movie.director },
        "actor": movie.actors.map((a) => ({ "@type": "Person", "name": a })),
        "genre": movie.genres,
        "url": `https://renrencinema.com/detail/${movie.id}`
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://renrencinema.com/" },
          { "@type": "ListItem", "position": 2, "name": categories.find((c) => c.slug === movie.category)?.name || movie.category, "item": `https://renrencinema.com/category/${movie.category}` },
          { "@type": "ListItem", "position": 3, "name": movie.title, "item": `https://renrencinema.com/detail/${movie.id}` }
        ]
      }
    ] : [],
  });

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </span>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">影片未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您要查找的影视资源不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors duration-200"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const catInfo = categories.find((c) => c.slug === movie.category);
  const relatedMovies = (detail?.relatedIds || [])
    .map((rid) => moviesMap[rid])
    .filter(Boolean);

  const formatNumber = (n: number): string => {
    if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
    return String(n);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: catInfo?.name || movie.category, href: `/category/${movie.category}` },
                { label: movie.title },
              ]}
            />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
              <div className="lg:w-72 shrink-0">
                <div className="rounded-xl overflow-hidden aspect-[2/3]">
                  <img
                    src={movie.imageUrl}
                    alt={movie.title}
                    title={`${movie.title} - 人人影院高清免费在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                  {movie.title}
                </h1>
                <div className="flex items-center flex-wrap gap-2 mb-3">
                  <span className="bg-primary-100 text-primary-700 text-xs font-bold px-2 py-0.5 rounded whitespace-nowrap">
                    {movie.rating}
                  </span>
                  {movie.isHot && (
                    <span className="bg-accent-500 text-background-50 text-xs font-medium px-2 py-0.5 rounded whitespace-nowrap">
                      热门
                    </span>
                  )}
                  {movie.isNew && (
                    <span className="bg-accent-100 text-accent-800 text-xs font-medium px-2 py-0.5 rounded whitespace-nowrap">
                      最新
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 mb-5 text-sm">
                  <div>
                    <span className="text-foreground-500">导演：</span>
                    <span className="text-foreground-900">{movie.director}</span>
                  </div>
                  <div>
                    <span className="text-foreground-500">类型：</span>
                    <span className="text-foreground-900">{movie.genres.join(' / ')}</span>
                  </div>
                  <div>
                    <span className="text-foreground-500">年份：</span>
                    <span className="text-foreground-900">{movie.year}</span>
                  </div>
                  <div>
                    <span className="text-foreground-500">地区：</span>
                    <span className="text-foreground-900">{movie.region}</span>
                  </div>
                  <div>
                    <span className="text-foreground-500">时长：</span>
                    <span className="text-foreground-900">{movie.duration}</span>
                  </div>
                  <div>
                    <span className="text-foreground-500">更新：</span>
                    <span className="text-foreground-900">{movie.updateDate}</span>
                  </div>
                </div>

                <div className="mb-2">
                  <span className="text-sm text-foreground-500">主演：</span>
                  <span className="text-sm text-foreground-900">{movie.actors.join('、')}</span>
                </div>

                {detail && (
                  <div className="flex items-center gap-6 mt-4 text-xs text-foreground-500">
                    <span>播放量：{detail.playCount}</span>
                    <span>评论数：{formatNumber(detail.commentCount)}</span>
                    <span>点赞数：{formatNumber(detail.likeCount)}</span>
                  </div>
                )}

                <div className="mt-5">
                  <button className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-full text-sm font-bold whitespace-nowrap cursor-pointer transition-all duration-200">
                    <span className="w-5 h-5 inline-flex items-center justify-center">
                      <i className="ri-play-circle-fill text-lg"></i>
                    </span>
                    立即播放
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-8 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-lg font-bold text-foreground-900 font-heading mb-4">剧情简介</h2>
            <div className="bg-background-50 rounded-xl p-5">
              <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                {detail?.fullDescription || movie.description}
              </p>
            </div>
          </div>
        </section>

        {relatedMovies.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-lg font-bold text-foreground-900 font-heading mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedMovies.map((rm) => (
                  <MovieCard key={rm.id} movie={rm} variant="compact" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}